import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AccountingProfitAndLossModel } from '../models/accounting-profitandloss.model';
import { AccountingBalanceSheetEquityAndLiabilitiesModel } from '../models/accounting-balancesheet-equityandliabilities.model';
import { AccountingBalanceSheetActiveModel } from '../models/accounting-balancesheet-active.model';
import { SumasySaldosGroupModel, SumasySaldosView } from '../models/sumasysaldosGroup.model';
import { ApiResponse } from '../../../models/base';

const ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT = '/accounting/reports/';

@Injectable({
  providedIn: 'root',
})
export class AccountingReportsService {
  constructor(private http: HttpClient) {}

  public getSumasySaldos(groupInfo: SumasySaldosGroupModel): Promise<any[]> {
    return firstValueFrom(
      this.http.post<ApiResponse<any[]>>(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'sumasysaldos',
        groupInfo
      )
    ).then((response) => response.data);
  }

  public getExcelSumasySaldos(
    sumasySaldosView: SumasySaldosView[],
    sumasySaldosGroupModel: SumasySaldosGroupModel
  ): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'sumasysaldos/excel',
        {
          sumasySaldosGroupAccounts: sumasySaldosGroupModel,
          sumasySaldosViews: sumasySaldosView,
        },
        { responseType: 'blob' }
      )
    ).then(
      (response) =>
        new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }) as Blob
    );
  }

  public getPdfSumasySaldos(
    sumasySaldosView: SumasySaldosView[],
    sumasySaldosGroupModel: SumasySaldosGroupModel
  ): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'sumasysaldos/pdf',
        {
          sumasySaldosGroupAccounts: sumasySaldosGroupModel,
          sumasySaldosViews: sumasySaldosView,
        },
        { responseType: 'blob' }
      )
    ).then((response) => new Blob([response], { type: 'application/pdf' }) as Blob);
  }

  public getProfitAndLossModel(
    global: boolean,
    from: string,
    to: string
  ): Promise<AccountingProfitAndLossModel> {
    let params = new HttpParams();
    params = params.append('global', global ? 'true' : 'false');
    params = params.append('from', from);
    params = params.append('to', to);

    return firstValueFrom(
      this.http.get<ApiResponse<AccountingProfitAndLossModel>>(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'profitandloss',
        { params }
      )
    ).then((response) => response.data);
  }

  public getPdfProfitAndLoss(request: any): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'profitandloss/pdf',
        request,
        { responseType: 'blob' }
      )
    ).then((response) => new Blob([response], { type: 'application/pdf' }) as Blob);
  }

  public getExcelProfitAndLoss(request: any): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'profitandloss/excel',
        request,
        { responseType: 'blob' }
      )
    ).then(
      (response) =>
        new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }) as Blob
    );
  }

  public getBalanceSheetActiveModel(
    global: boolean,
    from: string,
    to: string
  ): Promise<AccountingBalanceSheetActiveModel> {
    let params = new HttpParams();
    params = params.append('global', global ? 'true' : 'false');
    params = params.append('from', from);
    params = params.append('to', to);

    return firstValueFrom(
      this.http.get<ApiResponse<AccountingBalanceSheetActiveModel>>(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'balance/active',
        { params }
      )
    ).then((response) => response.data);
  }

  public getPdfBalanceSheetActive(request: any): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'balance/active/pdf',
        request,
        { responseType: 'blob' }
      )
    ).then((response) => new Blob([response], { type: 'application/pdf' }) as Blob);
  }

  public getExcelBalanceSheetActive(request: any): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl + ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT + 'balance/active/excel',
        request,
        { responseType: 'blob' }
      )
    ).then(
      (response) =>
        new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }) as Blob
    );
  }

  public getBalanceSheetEquityAndLiabilitiesModel(
    global: boolean,
    from: string,
    to: string
  ): Promise<AccountingBalanceSheetEquityAndLiabilitiesModel> {
    let params = new HttpParams();
    params = params.append('global', global ? 'true' : 'false');
    params = params.append('from', from);
    params = params.append('to', to);

    return firstValueFrom(
      this.http.get<ApiResponse<AccountingBalanceSheetEquityAndLiabilitiesModel>>(
        environment.apiUrl +
          ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT +
          'balance/equityandliabilities',
        { params }
      )
    ).then((response) => response.data);
  }

  public getPdfBalanceSheetEquityAndLiabilities(request: any): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl +
          ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT +
          'balance/equityandliabilities/pdf',
        request,
        { responseType: 'blob' }
      )
    ).then((response) => new Blob([response], { type: 'application/pdf' }) as Blob);
  }

  public getExcelBalanceSheetEquityAndLiabilities(request: any): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        environment.apiUrl +
          ACCOUNTING_ACCOUNTS_REPORTS_ENDPOINT +
          'balance/equityandliabilities/excel',
        request,
        { responseType: 'blob' }
      )
    ).then(
      (response) =>
        new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }) as Blob
    );
  }
}